// F&B Covers — daily meal-count planner for kitchen (Phase 2.3 hotel operations)
// Half-board property: every in-house guest = 1 breakfast + 1 dinner.
// Surfaces dietary flags parsed from folio notes for kitchen prep.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Coffee, Utensils, Users, RefreshCw, Loader2, Calendar,
  Leaf, AlertTriangle, Wheat, Printer,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const T = {
  en: {
    title: 'F&B Covers',
    subtitle: 'Kitchen prep – daily breakfast & dinner counts',
    date: 'Date',
    refresh: 'Refresh',
    print: 'Print',
    breakfast: 'Breakfast covers',
    dinner: 'Dinner covers',
    adults: 'Adults',
    children: 'Children',
    totalGuests: 'Total guests in-house',
    dietary: 'Dietary requirements',
    vegan: 'Vegan',
    vegetarian: 'Vegetarian',
    gluten: 'Gluten-free',
    allergy: 'Allergies',
    noDietary: 'No special dietary requirements flagged',
    empty: 'No guests in-house for this date',
    hint: 'Half-board means each in-house guest counts as one breakfast + one dinner. Dietary flags come from folio notes.',
  },
  me: {
    title: 'F&B – Obroci',
    subtitle: 'Priprema kuhinje – dnevni doručci i večere',
    date: 'Datum',
    refresh: 'Osvježi',
    print: 'Štampaj',
    breakfast: 'Doručaka',
    dinner: 'Večera',
    adults: 'Odrasli',
    children: 'Djeca',
    totalGuests: 'Ukupno gostiju u hotelu',
    dietary: 'Posebni zahtjevi',
    vegan: 'Vegan',
    vegetarian: 'Vegetarijanski',
    gluten: 'Bez glutena',
    allergy: 'Alergije',
    noDietary: 'Nema posebnih zahtjeva ishrane',
    empty: 'Nema gostiju u hotelu za ovaj datum',
    hint: 'Polupansion – svaki gost = 1 doručak + 1 večera. Alergije se čitaju iz napomena na računu.',
  },
  sq: {
    title: 'F&B – Vaktet',
    subtitle: 'Përgatitja e kuzhinës – numri ditor i mëngjeseve dhe darkave',
    date: 'Data',
    refresh: 'Rifresko',
    print: 'Printo',
    breakfast: 'Mëngjese',
    dinner: 'Darka',
    adults: 'Të rriturit',
    children: 'Fëmijë',
    totalGuests: 'Mysafirë në hotel',
    dietary: 'Kërkesa ushqimore',
    vegan: 'Vegan',
    vegetarian: 'Vegjetarian',
    gluten: 'Pa gluten',
    allergy: 'Alergji',
    noDietary: 'Nuk ka kërkesa të veçanta ushqimore',
    empty: 'S`ka mysafirë në hotel për këtë datë',
    hint: 'Gjysmë-pension – çdo mysafir = 1 mëngjes + 1 darkë. Alergjitë lexohen nga shënimet e faturës.',
  },
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const FbCard = ({ icon: Icon, label, value, color, testid, sub }) => {
  const colorMap = {
    orange: { icon: 'text-orange-500', value: 'text-orange-600' },
    red: { icon: 'text-red-500', value: 'text-red-600' },
    blue: { icon: 'text-blue-500', value: 'text-blue-600' },
    purple: { icon: 'text-purple-500', value: 'text-purple-600' },
  };
  const c = colorMap[color] || colorMap.orange;
  return (
    <div className="bg-white rounded-xl border p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 text-gray-500 text-xs mb-2 uppercase tracking-wide">
        <Icon className={`w-4 h-4 ${c.icon}`} /> {label}
      </div>
      <p className={`text-4xl font-bold ${c.value}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
};

const DietaryChip = ({ icon: Icon, label, count, color, testid }) => {
  const active = count > 0;
  const colorMap = {
    green: 'bg-green-100 text-green-700 border-green-200',
    lime: 'bg-lime-100 text-lime-700 border-lime-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    red: 'bg-red-100 text-red-700 border-red-200',
  };
  const cls = active ? (colorMap[color] || colorMap.green) : 'bg-gray-50 text-gray-400 border-gray-100';
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cls}`} data-testid={testid}>
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium">{label}</span>
      <span className={`ml-auto text-sm font-bold ${active ? '' : 'text-gray-300'}`}>{count}</span>
    </div>
  );
};

const FbCovers = () => {
  const [uiLang, setUiLang] = useState(() => localStorage.getItem('reception_lang') || 'en');
  const t = T[uiLang] || T.en;
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/reception/covers?date_str=${date}`, {
        credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Failed to load');
      setData(json);
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const flags = data?.dietary_flags || { vegan: 0, vegetarian: 0, gluten_free: 0, allergy: 0 };
  const hasDietary = flags.vegan + flags.vegetarian + flags.gluten_free + flags.allergy > 0;

  const dietaryChips = useMemo(() => ([
    { key: 'vegan', icon: Leaf, label: t.vegan, count: flags.vegan, color: 'green', testid: 'fb-chip-vegan' },
    { key: 'vegetarian', icon: Leaf, label: t.vegetarian, count: flags.vegetarian, color: 'lime', testid: 'fb-chip-vegetarian' },
    { key: 'gluten', icon: Wheat, label: t.gluten, count: flags.gluten_free, color: 'amber', testid: 'fb-chip-gluten' },
    { key: 'allergy', icon: AlertTriangle, label: t.allergy, count: flags.allergy, color: 'red', testid: 'fb-chip-allergy' },
  ]), [flags, t]);

  const total = data?.total_guests ?? 0;
  const isEmpty = !loading && total === 0;

  return (
    <div className="space-y-6" data-testid="fb-covers-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Utensils className="w-7 h-7 text-orange-500" /> {t.title}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={uiLang}
            onChange={(e) => { setUiLang(e.target.value); localStorage.setItem('reception_lang', e.target.value); }}
            className="px-3 py-1.5 border rounded text-sm"
            data-testid="fb-lang-picker"
          >
            <option value="en">EN</option>
            <option value="me">ME</option>
            <option value="sq">SQ</option>
          </select>
          <label className="flex items-center gap-2 text-sm bg-white border px-2 py-1 rounded">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="text-sm outline-none"
              data-testid="fb-date-picker"
            />
          </label>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50"
            data-testid="fb-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {t.refresh}
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 bg-orange-500 text-white px-3 py-1.5 rounded text-sm hover:bg-orange-600 font-semibold"
            data-testid="fb-print"
          >
            <Printer className="w-4 h-4" /> {t.print}
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="fb-error">
          {err}
        </div>
      )}

      {/* Primary cover counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <FbCard icon={Coffee} label={t.breakfast} value={data?.breakfast_covers ?? '—'} color="orange" testid="fb-kpi-breakfast" />
        <FbCard icon={Utensils} label={t.dinner} value={data?.dinner_covers ?? '—'} color="red" testid="fb-kpi-dinner" />
        <FbCard icon={Users} label={t.adults} value={data?.adults ?? '—'} color="blue" testid="fb-kpi-adults" />
        <FbCard icon={Users} label={t.children} value={data?.children ?? '—'} color="purple" testid="fb-kpi-children" />
      </div>

      {/* Total guests summary */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-100 rounded-xl p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-xl">
          {total}
        </div>
        <div>
          <p className="font-semibold text-gray-900">{t.totalGuests}</p>
          <p className="text-xs text-gray-600">{data?.date}</p>
        </div>
      </div>

      {/* Dietary requirements */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Leaf className="w-5 h-5 text-green-500" /> {t.dietary}
        </h3>
        {isEmpty ? (
          <p className="text-sm text-gray-400">{t.empty}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {dietaryChips.map((c) => (
                <DietaryChip key={c.key} icon={c.icon} label={c.label} count={c.count} color={c.color} testid={c.testid} />
              ))}
            </div>
            {!hasDietary && (
              <p className="text-xs text-gray-400 mt-3">{t.noDietary}</p>
            )}
          </>
        )}
      </div>

      <p className="text-xs text-gray-400 italic">{t.hint}</p>
    </div>
  );
};

export default FbCovers;
